import type {
    CLSAttribution,
    FCPAttribution,
    INPAttribution,
    LCPAttribution,
    MetricWithAttribution,
    TTFBAttribution,
} from "web-vitals/attribution";
import type {
    CountryType,
    Nullable,
    ObjectToUnionType,
} from "../@types/generic";

export type MobileWebType =
    | "web-app-react"
    | "web-app-react/ios"
    | "web-app-react/android"
    | "web-app-react/unknown";

// require "undefined" to be set to unset Overlay state when coming from another page
export type OverlayType =
    | undefined
    | "vhs"
    | "feedback"
    | "ski_livecam"
    | "ski_filter"
    | "inactive";

// require "undefined" to be set to unset Overlay state when coming from another page
type OverlayStatus = undefined | false | "open" | "close";

export type TeaserTackingItem = {
    id: string;
    name: string;
    target_url: string;
    position: number;
    type: string;
    format: string;
};

export type DataLayerItemPageTrack = {
    type: "pageTrack";
    event: "pagetrack";
    pageType?:
        | "home"
        | "forecast"
        | "ski_areas"
        | "livecams"
        | "health"
        | "governance"
        | "Accessibility";
    pageTypeDetail?:
        | "main"
        | "today"
        | "tomorrow"
        | "day_2"
        | "day_3"
        | "day_4"
        | "day_5"
        | "day_6"
        | "weekend"
        | "3_days"
        | "7_days"
        | "16_days"
        | "overview"
        | "detail"
        | "partnership_info"
        | "pollen"
        | "location_overview"
        | "location_livecam"
        | "location_weather"
        | "location_lifts"
        | "compliance"
        | "privacy"
        | "imprint"
        | "terms_of_use";
    pageName?: string;
    mobileWebType?: MobileWebType;
    pageCountry?: CountryType;
    pageRegion?: string;
    pageCity?: string;
    pageId?: string;
    tcontent?: string;
    tcdist?: string;
    overlay: OverlayStatus;
    overlayType: OverlayType;
    ab_6032_global?: "r" | "notset";
    ab_6032_r?: "tod" | "tom" | "wend" | "3" | "7" | "16" | "single" | "notset";
    ab_6462?: "t" | "c" | "f";
};

export type DataLayerItem = ObjectToUnionType<
    "type",
    {
        pageTrack: DataLayerItemPageTrack;
        overlay: {
            event: "overlay";
            overlay: OverlayStatus;
            overlayType?: OverlayType;
        };
        event: {
            event:
                | "ai~2MinutenLayer"
                | "ai~5MinutenLayer"
                | "ae~2MinutenLayer"
                | "ae~5MinutenLayer"
                | "clicktrack"
                | "ae~push"
                | "savedata"
                | "measurement"
                | "consent_status";
            eventCategory:
                | "function"
                | "teaser"
                | "Save Data Check"
                | "Web Vitals"
                | "gtag";
            eventAction: string | boolean;
            eventLabel: string;
            eventValue?: string | number;
            nonInteraction?: boolean;
        };
        webVitals: {
            event: "measurement";
            eventCategory: "Web Vitals";
            eventAction: MetricWithAttribution["name"];
            eventLabel: string;
            eventValue: number;
            attribution:
                | CLSAttribution
                | FCPAttribution
                | INPAttribution
                | LCPAttribution
                | TTFBAttribution;
        };
        adBlock: {
            adBlock: "Yes";
        };
        push: {
            type: "push";
            push: "unsubscribe" | "subscribe";
        };
        currentWeatherFeedback: {
            event: "feedback_current_weather";
            condition: Nullable<number>;
            temperature: Nullable<number>;
            suggestedCondition: number;
            suggestedTemperature: string;
            location: Nullable<string>;
            lat: number;
            long: number;
            run: Nullable<string>;
        };
        scrollEnd: {
            event: "slider_scroll_end";
            slider: string;
        };
        adReload: {
            event: "adReload";
            count: number;
        };
        teaserTracking: {
            event: "teaser_impression" | "teaser_click";
            teaser_list_id: string;
            teaser_list_name: string;
            teaser_list_type: string;
            teasers: Array<TeaserTackingItem | undefined>;
        };
    }
>;

export const isPageTrackDataLayerItem = (
    obj: DataLayerItem | undefined
): obj is DataLayerItemPageTrack =>
    (obj !== undefined &&
        Object.prototype.hasOwnProperty.call(obj, "pageType") &&
        Object.prototype.hasOwnProperty.call(obj, "pageTypeDetail")) ||
    false;
